import React from 'react';
import { Box, List, ListItem, Text, Flex, Circle } from '@chakra-ui/react';
import DOMPurify from 'dompurify';
import ImageComponent from './ImageComponent';

/**
 * ListComponent - Renders various types of lists with customizable styling
 * @param {Array} items - Array of list items (strings or objects)
 * @param {string} styleType - Type of list: 'disc', 'circle', 'square', 'decimal', 'lower-alpha', 'upper-alpha', 'lower-roman', 'upper-roman', 'custom-circle', 'solid-numbered-circle', 'outline-numbered-circle', 'image-list'
 * @param {string} variant - Typography variant for text styling
 * @param {string} itemColor - Color for list item text
 * @param {string} markerColor - Color for list markers/bullets
 * @param {number} marginTop - Top margin
 * @param {number} marginBottom - Bottom margin
 * @param {number} startNumber - Starting number for numbered lists (default: 1)
 * @param {Object} icon - Icon configuration for circle-based lists
 * @param {Object} image - Image configuration for image-list style
 */
const ListComponent = ({
  items,
  styleType = 'disc',
  variant = 'body-md-min-reg',
  itemColor,
  markerColor,
  marginTop,
  marginBottom,
  startNumber,
  icon = {
    iconVariant: 'icon-sm-solid',
    iconSize: null,
    iconBorder: null,
    iconTextVariant: null,
    iconTextColor: null,
    bgColor: null,
    color: null,
    borderColor: null,
  },
  image = null, // NEW: Image configuration for image-list style
}) => {
  if (!items || !Array.isArray(items)) return null;
  
  // Helper function to get the display number for an item
  const getDisplayNumber = (index) => {
    return startNumber + index;
  };
  
  // Render image list item (image + text layout)
  const renderImageListItem = (item, index, imageConfig) => {
    if (typeof item === 'string') {
      return (
        <Flex key={index} alignItems="center" gap={4} mb={3}>
          {imageConfig && (
            <Box
              width={imageConfig.size || '48px'}
              height={imageConfig.size || '48px'}
              flexShrink={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <ImageComponent
                image={imageConfig}
                variant="xs"
                rotation={0}
              />
            </Box>
          )}
          <Box flex="1" display="flex" alignItems="center">
            <Text
              fontFamily={`var(--${variant}-font-family, Roboto)`}
              fontSize={`var(--${variant}-font-size, 18px)`}
              fontWeight={`var(--${variant}-font-weight, 400)`}
              lineHeight={`var(--${variant}-line-height, 27px)`}
              color={`var(--${variant}-color, var(--text-primary-color))`}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item, { ADD_ATTR: ['target', 'rel'] }) }}
            />
          </Box>
        </Flex>
      );
    } else if (typeof item === 'object' && item.title && item.description) {
      return (
        <Flex key={index} alignItems="center" gap={4} mb={4}>
          {imageConfig && (
            <Box
              width={imageConfig.size || '48px'}
              height={imageConfig.size || '48px'}
              flexShrink={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <ImageComponent
                image={imageConfig}
                variant="xs"
                rotation={0}
              />
            </Box>
          )}
          <Box flex="1" display="flex" flexDirection="column" justifyContent="center">
            {item.title.content && (
              <Text
                fontFamily={`var(--${item.title.variant || variant}-font-family, Sora)`}
                fontWeight="bold"
                fontSize={`var(--${item.title.variant || variant}-font-size, 18px)`}
                lineHeight={`var(--${item.title.variant || variant}-line-height, 23.4px)`}
                color={item.title.color || `var(--${item.title.variant || variant}-color, var(--color-dusky-teal))`}
                mt={item.title.marginTop || 0}
                mb={item.title.marginBottom || 0}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.title.content, { ADD_ATTR: ['target', 'rel'] }) }}
              />
            )}
            {item.description.content && (
              <Text
                fontFamily={`var(--${item.description.variant || variant}-font-family, Roboto)`}
                fontSize={`var(--${item.description.variant || variant}-font-size, 18px)`}
                lineHeight={`var(--${item.description.variant || variant}-line-height, 27px)`}
                color={item.description.color || `var(--${item.description.variant || variant}-color, var(--color-dusky-teal))`}
                mt={item.description.marginTop || 0}
                mb={item.description.marginBottom || 2}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.description.content, { ADD_ATTR: ['target', 'rel'] }) }}
              />
            )}
          </Box>
        </Flex>
      );
    }
    return null;
  };
  
  const renderCircleItem = (content, index, currentIcon) => {
    const isOutline = styleType === 'outline-numbered-circle';

    return (
      <Flex key={index} alignItems="top" gap={3} mb={3}>
        <Circle
          size={currentIcon.iconSize || `var(--${currentIcon.iconVariant}-size, 36px)`}
          bg={
            currentIcon.bgColor ||
            `var(--${currentIcon.iconVariant}-bg-color, transparent)`
          }
          border={
            isOutline
              ? currentIcon.iconBorder || `var(--${currentIcon.iconVariant}-border, none)`
              : 'none'
          }
          borderColor={
            currentIcon.borderColor ||
            `var(--${currentIcon.iconVariant}-border-color, none)`
          }
          color={
            currentIcon.color ||
            `var(--${currentIcon.iconVariant}-color, white)`
          }
          fontWeight="bold"
        >
          <Text
            fontFamily={`var(--${currentIcon.iconTextVariant || variant}-font-family, Roboto)`}
            fontSize={`var(--${currentIcon.iconTextVariant || variant}-font-size, 16px)`}
            fontWeight={`var(--${currentIcon.iconTextVariant || variant}-font-weight, bold)`}
            lineHeight={`var(--${currentIcon.iconTextVariant || variant}-line-height, 24px)`}
            color={
              currentIcon.iconTextColor ||
              `var(--${currentIcon.iconTextVariant}-color, var(--color-dusky-teal))`
            }
          >
            {styleType !== 'custom-circle' ? getDisplayNumber(index) : null}
          </Text>
        </Circle>
        <Text
          fontFamily={`var(--${variant}-font-family, Roboto)`}
          fontSize={`var(--${variant}-font-size, 18px)`}
          fontWeight={`var(--${variant}-font-weight, 400)`}
          lineHeight={`var(--${variant}-line-height, 27px)`}
          color={`var(--${variant}-color, var(--text-primary-color))`}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content, { ADD_ATTR: ['target', 'rel'] }) }}
        />
      </Flex>
    );
  };

  return (
    <Box mb={marginBottom || 4}>
      {[
        'custom-circle',
        'solid-numbered-circle',
        'outline-numbered-circle',
        'image-list',
      ].includes(styleType) ? (
        items.map((item, index) => {
          if (styleType === 'image-list') {
            return renderImageListItem(item, index, image);
          } else if (typeof item === 'string') {
            return renderCircleItem(item, index, icon);
          } else if (typeof item === 'object' && item.type === 'list') {
            return (
              <Box key={index}>
                {item.title && (
                  <Text
                    fontFamily={`var(--${variant}-font-family, Roboto)`}
                    fontWeight="bold"
                    fontSize={`var(--${variant}-font-size, 18px)`}
                    lineHeight={`var(--${variant}-line-height, 27px)`}
                    color={
                      itemColor ||
                      `var(--${variant}-color, var(--sutter-sync-purple))`
                    }
                    mb={2}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(item.title, { ADD_ATTR: ['target', 'rel'] }),
                    }}
                  />
                )}
                <ListComponent
                  items={item.items}
                  styleType={item.styleType || 'custom-circle'}
                  variant={item.variant || variant}
                  startNumber={item.startNumber || startNumber}
                  icon={{
                    ...icon,
                    ...item.icon,
                  }}
                  markerColor={
                    item.markerColor ||
                    markerColor ||
                    `var(--sutter-sync-purple)`
                  }
                  itemColor={item.itemColor || `var(--${variant}-color)`}
                />
              </Box>
            );
          } else if (typeof item === 'object' && item.title && item.description) {
            const isOutline = styleType === 'outline-numbered-circle';
            return (
              <Flex key={index} alignItems="flex-start" gap={3} mb={4} flexDirection="row">
                <Circle
                  size={icon.iconSize || `var(--${icon.iconVariant}-size, 36px)`}
                  bg={icon.bgColor || `var(--${icon.iconVariant}-bg-color, transparent)`}
                  border={isOutline ? icon.iconBorder || `var(--${icon.iconVariant}-border, none)` : 'none'}
                  borderColor={icon.borderColor || `var(--${icon.iconVariant}-border-color, none)`}
                  color={icon.color || `var(--${icon.iconVariant}-color, white)`}
                  fontWeight="bold"
                >
                  <Text
                    fontFamily={`var(--${icon.iconTextVariant || variant}-font-family, Roboto)`}
                    fontSize={`var(--${icon.iconTextVariant || variant}-font-size, 16px)`}
                    fontWeight={`var(--${icon.iconTextVariant || variant}-font-weight, bold)`}
                    lineHeight={`var(--${icon.iconTextVariant || variant}-line-height, 24px)`}
                    color={icon.iconTextColor || `var(--${icon.iconTextVariant}-color, var(--color-dusky-teal))`}
                  >
                    {styleType !== 'custom-circle' ? getDisplayNumber(index) : null}
                  </Text>
                </Circle>
          
                <Box flex="1"> {/* Container for title and description */}
                  {item.title.content && (
                    <Text
                      fontFamily={`var(--${item.title.variant || variant}-font-family, Sora)`}
                      fontWeight="bold"
                      fontSize={`var(--${item.title.variant || variant}-font-size, 18px)`}
                      lineHeight={`var(--${item.title.variant || variant}-line-height, 23.4px)`}
                      color={item.title.color || `var(--${item.title.variant || variant}-color, var(--color-dusky-teal))`}
                      mt={item.title.marginTop || 0}
                      mb={item.title.marginBottom || 0}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.title.content, { ADD_ATTR: ['target', 'rel'] }) }}
                    />
                  )}
                  {item.description.content && (
                    <Text
                      fontFamily={`var(--${item.description.variant || variant}-font-family, Roboto)`}
                      fontSize={`var(--${item.description.variant || variant}-font-size, 18px)`}
                      lineHeight={`var(--${item.description.variant || variant}-line-height, 27px)`}
                      color={item.description.color || `var(--${item.description.variant || variant}-color, var(--color-dusky-teal))`}
                      mt={item.description.marginTop || 0}
                      mb={item.description.marginBottom || 2}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.description.content, { ADD_ATTR: ['target', 'rel'] }) }}
                    />
                  )}
                </Box>
              </Flex>
            );
          } else {
            console.warn('Unknown list item format:', item);
            return null;
          }
        })
      ) : (
        <List
          as={['decimal', 'lower-alpha', 'upper-alpha', 'lower-roman', 'upper-roman'].includes(styleType) ? 'ol' : 'ul'}
          pl={4}
          mt={marginTop}
          start={['decimal', 'lower-alpha', 'upper-alpha', 'lower-roman', 'upper-roman'].includes(styleType) && startNumber > 1 ? startNumber : undefined}
          sx={{
            listStyleType: styleType,
            listStylePosition: 'outside',
            'li::marker': {
              color: markerColor || `var(--sutter-sync-purple)`,
            },
          }}
        >
          {items.map((item, index) => {
            if (typeof item === 'string') {
              return (
                <ListItem
                  key={index}
                  fontFamily={`var(--${variant}-font-family, Roboto)`}
                  fontSize={`var(--${variant}-font-size, 18px)`}
                  fontWeight={`var(--${variant}-font-weight, 400)`}
                  lineHeight={`var(--${variant}-line-height, 27px)`}
                  color={itemColor || `var(--${variant}-color)`}
                  mb={1}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item, { ADD_ATTR: ['target', 'rel'] }) }}
                />
              );
            } else if (typeof item === 'object' && item.type === 'list') {
              return (
                <Box key={index} mt={marginTop}>
                  {item.title && (
                    <Text
                      fontFamily={`var(--${variant}-font-family, Roboto)`}
                      fontWeight="bold"
                      fontSize={`var(--${variant}-font-size, 18px)`}
                      lineHeight={`var(--${variant}-line-height, 27px)`}
                      color={itemColor || `var(--${variant}-color)`}
                      mb={2}
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(item.title, { ADD_ATTR: ['target', 'rel'] }),
                      }}
                    />
                  )}
                  <ListComponent
                    items={item.items}
                    styleType={item.styleType || 'disc'}
                    variant={item.variant || variant}
                    startNumber={item.startNumber || startNumber}
                    markerColor={item.markerColor || markerColor}
                    itemColor={item.itemColor || `var(--${variant}-color)`}
                  />
                </Box>
              );
            } else if (typeof item === 'object' && item.title && item.description) {
              const isOutline = styleType === 'outline-numbered-circle';
              return (
                <Flex key={index} alignItems="flex-start" gap={3} mb={4} flexDirection="row">
                  <Circle
                    size={icon.iconSize || `var(--${icon.iconVariant}-size, 36px)`}
                    bg={icon.bgColor || `var(--${icon.iconVariant}-bg-color, transparent)`}
                    border={isOutline ? icon.iconBorder || `var(--${icon.iconVariant}-border, none)` : 'none'}
                    borderColor={icon.borderColor || `var(--${icon.iconVariant}-border-color, none)`}
                    color={icon.color || `var(--${icon.iconVariant}-color, white)`}
                    fontWeight="bold"
                  >
                    <Text
                      fontFamily={`var(--${icon.iconTextVariant || variant}-font-family, Roboto)`}
                      fontSize={`var(--${icon.iconTextVariant || variant}-font-size, 16px)`}
                      fontWeight={`var(--${icon.iconTextVariant || variant}-font-weight, bold)`}
                      lineHeight={`var(--${icon.iconTextVariant || variant}-line-height, 24px)`}
                      color={icon.iconTextColor || `var(--${icon.iconTextVariant}-color, var(--color-dusky-teal))`}
                    >
                      {styleType !== 'custom-circle' ? getDisplayNumber(index) : null}
                    </Text>
                  </Circle>
            
                  <Box flex="1"> {/* Container for title and description */}
                    {item.title.content && (
                      <Text
                        fontFamily={`var(--${item.title.variant || variant}-font-family, Sora)`}
                        fontWeight="bold"
                        fontSize={`var(--${item.title.variant || variant}-font-size, 18px)`}
                        lineHeight={`var(--${item.title.variant || variant}-line-height, 23.4px)`}
                        color={item.title.color || `var(--${item.title.variant || variant}-color, var(--color-dusky-teal))`}
                        mt={item.title.marginTop || 0}
                        mb={item.title.marginBottom || 0}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.title.content, { ADD_ATTR: ['target', 'rel'] }) }}
                      />
                    )}
                    {item.description.content && (
                      <Text
                        fontFamily={`var(--${item.description.variant || variant}-font-family, Roboto)`}
                        fontSize={`var(--${item.description.variant || variant}-font-size, 18px)`}
                        lineHeight={`var(--${item.description.variant || variant}-line-height, 27px)`}
                        color={item.description.color || `var(--${item.description.variant || variant}-color, var(--color-dusky-teal))`}
                        mt={item.description.marginTop || 0}
                        mb={item.description.marginBottom || 2}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.description.content, { ADD_ATTR: ['target', 'rel'] }) }}
                      />
                    )}
                  </Box>
                </Flex>
              );
            } else {
              console.warn('Unknown list item format:', item);
              return null;
            }
          })}
        </List>
      )}
    </Box>
  );
};

export default ListComponent;
